import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { Alert, Button, Card, DatePicker, Input, InputNumber, Select, Spin, Statistic, Tag, Typography } from 'antd';
import { useSelector } from 'react-redux';
import { toast } from 'react-toastify';
import dayjs from 'dayjs';
import axios from 'axios';
import config from '../../config';
import './ndoeTokens.css';
import CataloguePicker from '../journalTransactions/CataloguePicker';
import useRefLists from '../rapports/useRefLists';
import { NdoePage, NdoePageHeader, NdoeResponsiveTable, NdoeDataCard } from '../../components/ndoe';
import { messageErreur } from '../../components/ndoe/chargement';

const { Title, Text } = Typography;
const DOMAIN = config.REACT_APP_SERVER_DOMAIN;
const isYmd = (v) => /^\d{4}-\d{2}-\d{2}$/.test(v || '');
const frDate = (d) => (d ? new Date(d).toLocaleDateString('fr-FR') : '—');
const libelle = (v) => `${v.nom_produit}${v.taille ? ' · T' + v.taille : ''}${v.couleur ? ' · ' + v.couleur : ''}`;

const StockCorrectionAdmin = () => {
  const role = useSelector((s) => s.user?.currentUser?.role);
  const token = useSelector((s) => s.user?.currentUser?.accessToken || s.user?.currentUser?.token);
  const headers = useMemo(() => (token ? { authorization: `Bearer ${token}`, token: `Bearer ${token}` } : {}), [token]);

  const [options, setOptions] = useState([]);
  const [searching, setSearching] = useState(false);
  const [selected, setSelected] = useState(null);
  const [physique, setPhysique] = useState(null);
  const [commentaire, setCommentaire] = useState('');
  const [classification, setClassification] = useState(undefined);
  const [classifications, setClassifications] = useState([]);
  const [erreurClassif, setErreurClassif] = useState(null);
  const [dateInv, setDateInv] = useState('');
  const [saving, setSaving] = useState(false);
  const [history, setHistory] = useState([]);
  const [loadingHist, setLoadingHist] = useState(false);
  const seqRef = useRef(0);
  const refs = useRefLists(role === 'admin');
  const [pickerOpen, setPickerOpen] = useState(false);

  const loadHistory = useCallback(async () => {
    setLoadingHist(true);
    try {
      const { data } = await axios.get(`${DOMAIN}/api/stock/corrections`, { params: { limit: 50 }, headers });
      setHistory(data.corrections || []);
    } catch (e) {
      /* historique non bloquant */
    } finally {
      setLoadingHist(false);
    }
  }, [headers]);

  useEffect(() => { if (role === 'admin') loadHistory(); }, [role, loadHistory]);

  // Classifications d'écart partagées avec le module Inventaires (paramétrables).
  useEffect(() => {
    if (role !== 'admin') return;
    axios.get(`${DOMAIN}/api/inventaires/config`, { headers })
      .then(({ data }) => setClassifications((data.classifications || []).map((c) => ({ value: c.code, label: c.label }))))
      .catch((e) => { setClassifications([]); setErreurClassif(messageErreur(e, 'les motifs d’écart')); });
  }, [role, headers]);

  const doSearch = async (q) => {
    if (!q || q.trim().length < 2) { setOptions([]); return; }
    const seq = ++seqRef.current;
    setSearching(true);
    try {
      const { data } = await axios.get(`${DOMAIN}/api/stock/variantes`, { params: { q, limit: 25 }, headers });
      if (seq !== seqRef.current) return; // ignore les réponses obsolètes
      setOptions((data.variantes || []).map((v) => ({
        value: v.id_varianteProduit,
        label: `${libelle(v)} — stock ${v.stock_theorique}`,
        raw: v,
      })));
    } catch (e) {
      toast.error('Recherche impossible.');
    } finally {
      if (seq === seqRef.current) setSearching(false);
    }
  };

  const onSelect = (_value, opt) => { setSelected(opt.raw); setPhysique(null); };

  const ecart = selected && physique !== null && physique !== '' ? Number(physique) - Number(selected.stock_theorique) : null;
  const ecartTag = ecart === null ? <Text type="secondary">—</Text>
    : ecart === 0 ? <Tag>Aucun écart</Tag>
      : <Tag color={ecart > 0 ? 'green' : 'red'}>{ecart > 0 ? '+' : ''}{ecart}</Tag>;

  const submit = async () => {
    if (!selected) return toast.warn('Sélectionnez une variante.');
    if (physique === null || physique === '' || Number(physique) < 0 || !Number.isInteger(Number(physique))) {
      return toast.warn('Saisissez un stock physique entier (≥ 0).');
    }
    if (dateInv && !isYmd(dateInv)) return toast.warn('Date d’inventaire au format AAAA-MM-JJ.');
    setSaving(true);
    try {
      const { data } = await axios.post(
        `${DOMAIN}/api/stock/variante/${selected.id_varianteProduit}/correction`,
        {
          stock_physique: Number(physique), commentaire: commentaire || null,
          date_inventaire: dateInv || null, classification: classification || null,
        },
        { headers },
      );
      toast.success(data.message || 'Enregistré.');
      setSelected((s) => ({ ...s, stock_theorique: data.result.stock_physique }));
      setPhysique(null); setCommentaire(''); setClassification(undefined);
      loadHistory();
    } catch (e) {
      toast.error(e?.response?.data?.error || 'Correction échouée.');
    } finally {
      setSaving(false);
    }
  };

  // Colonnes tableau (desktop) — conservées à l'identique.
  const historyColumns = [
    { title: 'Date inv.', dataIndex: 'date_inventaire', render: frDate, width: 110 },
    { title: 'Produit', render: (_, r) => libelle(r) },
    { title: 'Théo.', dataIndex: 'stock_theorique', align: 'right', width: 70 },
    { title: 'Phys.', dataIndex: 'stock_physique', align: 'right', width: 70 },
    {
      title: 'Écart', dataIndex: 'ecart', align: 'right', width: 90,
      render: (e) => <Tag color={e === 0 ? 'default' : e > 0 ? 'green' : 'red'}>{e > 0 ? '+' : ''}{e}</Tag>,
    },
    {
      title: 'Motif', dataIndex: 'classification', width: 150,
      render: (c) => (c ? (classifications.find((x) => x.value === c)?.label || c) : '—'),
    },
    { title: 'Commentaire', dataIndex: 'commentaire', ellipsis: true },
  ];

  // Carte "correction" (mobile) — remplace une ligne du tableau.
  const renderHistoryCard = (r) => {
    const e = r.ecart;
    const motif = r.classification ? (classifications.find((x) => x.value === r.classification)?.label || r.classification) : null;
    return (
      <NdoeDataCard
        title={libelle(r)}
        subtitle={`Inventaire · ${frDate(r.date_inventaire)}`}
        badge={<Tag color={e === 0 ? 'default' : e > 0 ? 'green' : 'red'}>{e > 0 ? '+' : ''}{e}</Tag>}
        metrics={[
          { value: r.stock_theorique, label: 'Théo.' },
          { value: r.stock_physique, label: 'Phys.' },
          ...(motif ? [{ value: motif, label: 'Motif' }] : []),
        ]}
      >
        {r.commentaire ? (
          <p style={{ margin: '4px 0 0', color: 'var(--ndoe-muted, #6b7280)', fontSize: 13 }}>{r.commentaire}</p>
        ) : null}
      </NdoeDataCard>
    );
  };

  if (role !== 'admin') {
    return <div style={{ padding: 24 }}><Alert type="warning" showIcon message="Accès administrateur requis" /></div>;
  }

  return (
    <NdoePage style={{ maxWidth: 920, margin: '0 auto' }}>
      <NdoePageHeader
        eyebrow="Stock"
        title="Correction de stock (inventaire)"
        subtitle="Sélectionnez une variante et saisissez le stock physique constaté : le système compare au stock théorique et corrige avec une écriture d’ajustement tracée. L’écart et la date sont conservés."
      />

      <Card style={{ marginTop: 4, borderRadius: 'var(--ndoe-radius-sub, 14px)' }}>
        <Select
          showSearch
          filterOption={false}
          onSearch={doSearch}
          onChange={onSelect}
          placeholder="Rechercher un produit (min. 2 lettres)…"
          style={{ width: '100%' }}
          notFoundContent={searching ? <Spin size="small" /> : null}
          options={options}
          value={selected ? selected.id_varianteProduit : undefined}
        />
        <Button style={{ marginTop: 8 }} onClick={() => setPickerOpen(true)}>🖼 Chercher dans la galerie</Button>

        {selected && (
          <>
            <div style={{ marginTop: 20, display: 'flex', gap: 28, flexWrap: 'wrap', alignItems: 'flex-end' }}>
              <Statistic title="Stock théorique" value={selected.stock_theorique} />
              <div>
                <div style={{ marginBottom: 4 }}><Text type="secondary">Stock physique constaté</Text></div>
                <InputNumber inputMode="decimal" min={0} step={1} precision={0} value={physique} onChange={setPhysique} style={{ width: 160 }} />
              </div>
              <div>
                <div style={{ marginBottom: 4 }}><Text type="secondary">Écart</Text></div>
                <div style={{ fontSize: 18, lineHeight: '32px' }}>{ecartTag}</div>
              </div>
            </div>

            <div style={{ marginTop: 16, display: 'flex', gap: 16, flexWrap: 'wrap', alignItems: 'flex-end' }}>
              <div>
                <div style={{ marginBottom: 4 }}><Text type="secondary">Date d’inventaire</Text></div>
                <DatePicker
                  value={dateInv ? dayjs(dateInv) : null}
                  onChange={(d) => setDateInv(d ? d.format('YYYY-MM-DD') : '')}
                  format="YYYY-MM-DD"
                  placeholder="AAAA-MM-JJ (défaut : aujourd’hui)"
                  style={{ width: 220 }}
                />
              </div>
              {/* Le champ ne DISPARAÎT plus quand la liste des motifs n'arrive
                  pas : il reste là et dit pourquoi il est vide. Sinon on
                  corrigeait un stock sans jamais voir qu'un motif était attendu. */}
              {(classifications.length > 0 || erreurClassif) && (
                <div>
                  <div style={{ marginBottom: 4 }}><Text type="secondary">Pourquoi cet écart ?</Text></div>
                  <Select
                    allowClear
                    placeholder="Classification"
                    options={classifications}
                    value={classification}
                    onChange={setClassification}
                    notFoundContent={erreurClassif || undefined}
                    style={{ width: 240 }}
                  />
                </div>
              )}
              <div style={{ flex: 1, minWidth: 220 }}>
                <div style={{ marginBottom: 4 }}><Text type="secondary">Commentaire (optionnel)</Text></div>
                <Input value={commentaire} onChange={(e) => setCommentaire(e.target.value)} placeholder="ex. recomptage rayon" maxLength={255} />
              </div>
            </div>

            <Button type="primary" loading={saving} onClick={submit} style={{ marginTop: 18, background: 'var(--ndoe-ink)', borderColor: 'var(--ndoe-ink)' }}>
              {ecart === 0 ? 'Enregistrer l’inventaire' : 'Corriger le stock'}
            </Button>
          </>
        )}
      </Card>

      <div style={{ marginTop: 20 }}>
        <Title level={4} style={{ color: 'var(--ndoe-ink)' }}>Historique des corrections</Title>
        <NdoeResponsiveTable
          data={history}
          columns={historyColumns}
          rowKey="id_correction"
          loading={loadingHist}
          pagination={{ pageSize: 10, hideOnSinglePage: true }}
          scroll={{ x: 'max-content' }}
          renderCard={renderHistoryCard}
          empty={{ icon: '📋', title: 'Aucune correction', text: 'Les corrections enregistrées apparaîtront ici.' }}
        />
      </div>

      <CataloguePicker open={pickerOpen} onClose={() => setPickerOpen(false)} refLists={refs}
        onSelect={(v) => {
          const raw = {
            id_varianteProduit: v.id_varianteProduit, nom_produit: v.nom_produit,
            taille: v.taille, couleur: v.couleur, stock_theorique: Number(v.physique),
          };
          setOptions([{ value: raw.id_varianteProduit, label: `${libelle(raw)} — stock ${raw.stock_theorique}`, raw }]);
          setSelected(raw);
          setPhysique(null);
          setPickerOpen(false);
        }} />
    </NdoePage>
  );
};

export default StockCorrectionAdmin;
